import type { ProviderPatientStatement } from '../../../api/server/models/ProviderPatientStatement';
import type { ProviderClaim } from '../../../api/server/models/ProviderClaim';
import type { BalanceBreakdown } from '../balance-breakdown';
import type { StatementCodeKeyItem } from '../components/statement-code-key';
import {
    getStatementClaimStatusLabel,
    getStatementStatusTone,
} from '../components/statement-claim-cards.utils';
import { formatCurrency, formatMoneyOrEmpty, formatStatementDate, parseMoney } from '../utils/statement-format.utils';
import {
    resolvePendingAmountDueByClaimId,
    sumPendingAmountDueAdjustment,
} from '../utils/statement-pending-amount-due.utils';
import type {
    StatementPrintActivityRow,
    StatementPrintLedgerRow,
    StatementPrintSummaryRow,
    StatementPrintViewModel,
} from './statement-print.types';

const amountField = (val?: { amount?: string } | string): string => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    return val.amount || '';
};

const formatAddress = (
    address?:
        | {
              lines?: string[];
              city?: string;
              state?: string;
              postal?: string;
              country?: string;
          }
        | null,
): string => {
    if (!address) return '—';
    const lines = (address.lines || []).map(l => String(l || '').trim()).filter(Boolean);
    const city = String(address.city || '').trim();
    const state = String(address.state || '').trim();
    const postal = String(address.postal || '').trim();
    const country = String(address.country || '').trim();
    const statePostal = [state, postal].filter(Boolean).join(' ');
    const cityLine = [city, statePostal].filter(Boolean).join(city && statePostal ? ', ' : '');
    const parts = [...lines, cityLine, country].filter(Boolean);
    return parts.join(', ') || '—';
};

const patientFullName = (statement?: ProviderPatientStatement): string => {
    const p = statement?.patient;
    if (!p) return '—';
    return [p.first, p.middle, p.last, p.suffix].filter(Boolean).join(' ').trim() || '—';
};

const formatDisplayDate = (iso?: string): string => {
    if (!iso) return '—';
    // Prefer YYYY-MM-DD via formatStatementDate; fall back for full ISO
    if (/^\d{4}-\d{2}-\d{2}/.test(iso)) {
        return formatStatementDate(iso.slice(0, 10));
    }
    try {
        return new Date(iso).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return '—';
    }
};

const buildSummaryRows = (
    breakdown: BalanceBreakdown | undefined,
    amountDue: number,
): StatementPrintSummaryRow[] => {
    const totalCharged = breakdown?.totalCharged ?? 0;
    const insurancePaid = breakdown?.insurancePaid ?? 0;
    const planAdjustments = breakdown?.planAdjustments ?? 0;
    const patientPaid = breakdown?.patientPaid ?? 0;

    const rows: StatementPrintSummaryRow[] = [
        { label: 'Total charges', amountText: formatCurrency(totalCharged.toFixed(2)) },
        {
            label: 'Less — insurance payments',
            amountText: formatCurrency((-insurancePaid).toFixed(2)),
            credit: true,
        },
        {
            label: 'Less — plan adjustments',
            amountText: formatCurrency((-planAdjustments).toFixed(2)),
            credit: true,
        },
        {
            label: 'Less — payments & discounts',
            amountText: formatCurrency((-patientPaid).toFixed(2)),
            credit: true,
        },
        {
            label: 'Amount due',
            amountText: formatCurrency(amountDue.toFixed(2)),
            emphasize: true,
        },
    ];

    return rows;
};

const buildActivityRows = (
    ledger: StatementPrintLedgerRow[],
    patientStatement: ProviderPatientStatement | undefined,
    providerClaimsById: Map<string, ProviderClaim>,
    pendingAmountDueByClaimId: Map<string, number>,
): StatementPrintActivityRow[] => {
    const statementClaimsById = new Map(
        (patientStatement?.claims || []).filter(c => c.id).map(c => [c.id as string, c]),
    );

    return ledger.map(row => {
        const chargeRaw = amountField(row.charge);
        const creditRaw = amountField(row.credit);
        const pendingAmountDue =
            row.type === 'balance' && row.claimId ? pendingAmountDueByClaimId.get(row.claimId) : undefined;
        const balanceRaw = pendingAmountDue === undefined ? amountField(row.balance) : pendingAmountDue.toFixed(2);
        const codes = (row.diagnosisCodes || []).filter(Boolean);

        let creditText = '';
        if (creditRaw && parseMoney(creditRaw) !== 0) {
            creditText = formatMoneyOrEmpty(creditRaw, { asCredit: true });
        }

        let statusLabel: string | undefined;
        let statusTone: 'warning' | 'default' | undefined;
        if (row.type === 'claim' && row.claimId) {
            const statementClaim = statementClaimsById.get(row.claimId);
            statusLabel = statementClaim
                ? getStatementClaimStatusLabel(statementClaim, providerClaimsById)
                : row.metadata?.pending
                  ? 'Pending'
                  : undefined;
            statusTone = statusLabel ? getStatementStatusTone(statusLabel) : undefined;
        }

        return {
            id: row.id,
            type: row.type,
            dateText: row.date ? formatDisplayDate(row.date) : '',
            descText: (row.description || '').replace(/\n/g, ' · '),
            chargeText:
                chargeRaw && parseMoney(chargeRaw) !== 0
                    ? formatMoneyOrEmpty(chargeRaw)
                    : '',
            creditText,
            balanceText:
                balanceRaw && parseMoney(balanceRaw) !== 0
                    ? formatMoneyOrEmpty(balanceRaw)
                    : '',
            codesText: codes.length ? codes.join('  ') : undefined,
            isClaimHeader: row.type === 'claim',
            isBalanceRow: row.type === 'balance',
            statusLabel,
            statusTone,
        };
    });
};

export type BuildStatementPrintViewModelArgs = {
    patientStatement?: ProviderPatientStatement;
    ledgerRows: StatementPrintLedgerRow[];
    balanceBreakdown?: BalanceBreakdown;
    amountDueOverride?: string | number;
    codeKeyItems?: StatementCodeKeyItem[];
    providerClaimsById?: Map<string, ProviderClaim>;
};

export const buildStatementPrintViewModel = (
    args: BuildStatementPrintViewModelArgs,
): StatementPrintViewModel => {
    const {
        patientStatement,
        ledgerRows,
        balanceBreakdown,
        amountDueOverride,
        codeKeyItems,
        providerClaimsById = new Map(),
    } = args;
    const provider = patientStatement?.billingProvider;
    const patient = patientStatement?.patient;

    // Charges still awaiting the payer count toward Amount Due on the printed statement (FE-161).
    // Only claims that are actually printed (inside the DOS filter) contribute.
    const statementClaims = patientStatement?.claims || [];
    const pendingAmountDueByClaimId = resolvePendingAmountDueByClaimId(statementClaims, providerClaimsById);
    const printedClaimIds = new Set(
        ledgerRows.filter(row => row.type === 'claim' && row.claimId).map(row => row.claimId as string),
    );
    const pendingAdjustment = sumPendingAmountDueAdjustment(
        statementClaims,
        pendingAmountDueByClaimId,
        printedClaimIds,
    );

    const amountDueRaw =
        parseMoney(amountDueOverride ?? balanceBreakdown?.currentBalance ?? patientStatement?.balanceDue?.amount) +
        pendingAdjustment;

    const statementDate = formatDisplayDate(
        balanceBreakdown?.statementDate || new Date().toISOString().slice(0, 10),
    );

    return {
        providerName: provider?.organization || '—',
        providerAddress: formatAddress(provider?.addresses?.[0]),
        providerEmail: provider?.email || '—',
        providerPhone: provider?.phone || '—',
        patientName: patientFullName(patientStatement),
        patientAddress: formatAddress(patient?.addresses?.[0]),
        externalId: patient?.externalId || '—',
        statementDate,
        amountDue: formatCurrency(amountDueRaw.toFixed(2)),
        amountDueRaw,
        // balanceBreakdown.paidUp only knows the server balance, so it would colour pending charges as paid.
        paidUp:
            pendingAdjustment !== 0
                ? amountDueRaw <= 0.005
                : (balanceBreakdown?.paidUp ?? amountDueRaw <= 0.005),
        summaryRows: buildSummaryRows(balanceBreakdown, amountDueRaw),
        activityRows: buildActivityRows(ledgerRows, patientStatement, providerClaimsById, pendingAmountDueByClaimId),
        codeKeyItems: codeKeyItems || [],
        generatedLabel: statementDate,
    };
};
